import { memo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { CheckoutConfirmationResponseType } from 'src/models/Checkout/Confirmation';
import { useTHelper } from 'src/utils/i18n';
import { Enums } from 'src/models/enums';

type Props = {
  model: CheckoutConfirmationResponseType['DeliverySubsection'];
};

export const CheckoutConfirmationDelivery = memo<Props>(({ model }) => {
  const locale = 'checkout.confirmation.delivery';
  const tHelper = useTHelper(locale);
  const { t } = useTranslation();

  const { Mail, Email, Other } = Enums.campaigns.DeliveryTypeCode;

  return (
    <>
      <h2 className="section-header">{tHelper('title')}</h2>
      <div className="card hoverable summary">
        <div className="donation-detail-header">{tHelper('delivery')}</div>
        <table className="table word-break-last-td-column">
          <tbody>
            {model.ShowDeliveryInstructions && (
              <tr className="donation-detail-delivery-instructions-table-row">
                <td className="roboto-medium text-nowrap">{tHelper('deliveryInformation')}</td>
                <td>
                  {model.DeliveryTypeCode && ([Mail, Email] as number[]).includes(model.DeliveryTypeCode) && (
                    <Trans
                      i18nKey={`${locale}.deliveryMail`}
                      values={{ singular: model.TerminologySingular?.toLowerCase() || '' }}
                    >
                      <span>1</span>
                    </Trans>
                  )}
                  {model.DeliveryTypeCode === Other && model.DeliveryInstructions}
                </td>
              </tr>
            )}

            {!model.IsShippingAddressEmpty && (
              <>
                <tr>
                  <td className="roboto-medium">{tHelper('name')}</td>
                  <td>
                    <span>{model.ShippingFirstName}</span>&nbsp;
                    <span>{model.ShippingLastName}</span>
                  </td>
                </tr>
                {model.ShippingOrganization && (
                  <tr>
                    <td className="roboto-medium">{tHelper('organization')}</td>
                    <td>
                      <span>{model.ShippingOrganization}</span>
                    </td>
                  </tr>
                )}
                {model.ShippingAddressLine1 && (
                  <tr>
                    <td className="roboto-medium">{tHelper('address')}</td>
                    <td>
                      <div>{model.ShippingAddressLine1}</div>
                      {model.ShippingAddressLine2 && <div>{model.ShippingAddressLine2}</div>}
                      {model.ShippingAddressLine3 && <div>{model.ShippingAddressLine3}</div>}
                    </td>
                  </tr>
                )}
                {model.ShippingCity && (
                  <tr>
                    <td className="roboto-medium">{tHelper('city')}</td>
                    <td>
                      <span>{model.ShippingCity}</span>
                    </td>
                  </tr>
                )}
                {model.ShippingCountryID && (
                  <tr>
                    <td className="roboto-medium">{tHelper('country')}</td>
                    <td>
                      <span>{model.ShippingCountry}</span>
                    </td>
                  </tr>
                )}
                {model.ShippingRegionName !== null && (
                  <tr>
                    <td className="roboto-medium">
                      {t(`common.${model.ShippingCountryID === 'US' ? 'stateUs' : 'stateCanada'}`)}
                    </td>
                    <td>
                      <span>{model.ShippingRegionName}</span>
                    </td>
                  </tr>
                )}
                {model.ShippingPostalCode && (
                  <tr>
                    <td className="roboto-medium">
                      {t(
                        `common.forms.${
                          model.ShippingCountryID === 'US' ? 'postalCodePlaceholder' : 'postalCodeCanadaPlaceholder'
                        }`
                      )}
                    </td>
                    <td>
                      <span>{model.ShippingPostalCode}</span>
                    </td>
                  </tr>
                )}
              </>
            )}
            {!model.IsShippingPhoneEmpty && (
              <tr>
                <td className="roboto-medium">{tHelper('phone')}</td>
                <td>
                  <span>{model.ShippingPhone}</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
});
